import {
  CloudUpload as CloudUploadIcon,
  Code as CodeIcon,
  ContentCopy as ContentCopyIcon,
  Settings as SettingsIcon,
  Upload as UploadIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { json } from "@codemirror/lang-json";
import { githubLight } from "@uiw/codemirror-theme-github";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CodeMirror from "@uiw/react-codemirror";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AdminDialog, PageShell, AdminSectionPanel, AppChip } from "@/components/common";
import { CustomerConfig, CustomerService } from "@/services/features/CustomerService";
import { StoreService } from "@/services/features/StoreService";

import { ConfigSummary } from "@/pages/operations/configs/components/ConfigSummary";

type ConfigTab = "summary" | "edit";

const EditConfig: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { configId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const customerService = CustomerService.getInstance();
  const storeService = StoreService.getInstance(); // Keep for image upload
  const jsonExtensions = useMemo(() => [json()], []);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<CustomerConfig | null>(null);
  const [configString, setConfigString] = useState("");
  const [savedConfigString, setSavedConfigString] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageUploadDialog, setImageUploadDialog] = useState<"general" | "banner" | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const activeTab: ConfigTab =
    new URLSearchParams(location.search).get("tab") === "edit" ? "edit" : "summary";
  const hasUnsavedChanges = configString !== savedConfigString;

  const navigateToTab = (tab: ConfigTab) => {
    const searchParams = new URLSearchParams(location.search);
    if (tab === "summary") {
      searchParams.delete("tab");
    } else {
      searchParams.set("tab", tab);
    }

    const search = searchParams.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" });
  };

  useEffect(() => {
    loadConfig();
  }, [configId]);

  const loadConfig = async () => {
    if (!configId) {
      setError(t("operations.configs.editConfig.errors.noConfigurationIdProvided"));
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await customerService.getCustomerConfigByCustomerId(Number(configId));
      if (response.success && response.data) {
        const serializedConfig = JSON.stringify(response.data, null, 2);
        setConfig(response.data);
        setConfigString(serializedConfig);
        setSavedConfigString(serializedConfig);
        setJsonError(null);
        document.title = t("operations.configs.editConfig.documentTitleWithCustomerName", {
          customerName: response.data.customerName,
        });
      } else {
        setError(t("operations.configs.editConfig.errors.failedLoadConfiguration"));
        enqueueSnackbar(t("operations.configs.editConfig.notifications.failedLoadConfiguration"), {
          variant: "error",
        });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t("operations.configs.editConfig.errors.failedLoadConfiguration");
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (file: File, type: "general" | "banner") => {
    if (!config) return;

    setIsUploading(true);
    setUploadError(null);
    setUploadedImageUrl(null);
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await storeService.uploadImage(formData);
      if (response.success && response.data) {
        if (type === "general") {
          // General images are stored separately from the customer configuration.
        } else {
          const newConfig = {
            ...config,
            shopperApp: {
              ...config.shopperApp,
              bannerImageUrl: response.data,
            },
          };
          setConfig(newConfig);
          setConfigString(JSON.stringify(newConfig, null, 2));
          setJsonError(null);
        }
        setUploadedImageUrl(response.data);
      } else {
        setUploadError(t("operations.configs.editConfig.notifications.failedUploadImage"));
      }
    } catch {
      setUploadError(t("operations.configs.editConfig.notifications.failedUploadImage"));
    } finally {
      setIsUploading(false);
      setIsDragOver(false);
    }
  };

  const handleUploadFileSelection = (file?: File) => {
    if (!file || !imageUploadDialog) return;
    void handleImageUpload(file, imageUploadDialog);
  };

  const handleCopyUploadedImageUrl = () => {
    if (uploadedImageUrl) {
      void navigator.clipboard.writeText(uploadedImageUrl);
    }
  };

  const openImageUploadDialog = (type: "general" | "banner") => {
    setImageUploadDialog(type);
    setUploadError(null);
    setUploadedImageUrl(null);
  };

  const handleConfigChange = (newValue: string) => {
    setConfigString(newValue);
    try {
      const parsed = JSON.parse(newValue);
      setConfig(parsed);
      setJsonError(null);
    } catch {
      setJsonError(t("operations.configs.editConfig.errors.invalidJson"));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!config || !configId || jsonError || !hasUnsavedChanges) return;

    setIsSubmitting(true);
    try {
      const response = await customerService.editCustomerConfig(Number(configId), configString);
      if (response.success) {
        setSavedConfigString(configString);
        enqueueSnackbar(
          t("operations.configs.editConfig.notifications.configurationUpdatedSuccessfully"),
          { variant: "success" },
        );
        navigateToTab("summary");
      } else {
        enqueueSnackbar(
          t("operations.configs.editConfig.notifications.failedUpdateConfiguration"),
          { variant: "error" },
        );
      }
    } catch {
      enqueueSnackbar(t("operations.configs.editConfig.notifications.failedUpdateConfiguration"), {
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PageShell
        breadcrumbs="admin"
        title={t("operations.configs.editConfig.title")}
        icon={<SettingsIcon />}
      >
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}
        >
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell
        breadcrumbs="admin"
        title={t("operations.configs.editConfig.title")}
        icon={<SettingsIcon />}
      >
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </PageShell>
    );
  }

  if (!config) {
    return (
      <PageShell
        breadcrumbs="admin"
        title={t("operations.configs.editConfig.title")}
        icon={<SettingsIcon />}
      >
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t("operations.configs.editConfig.text.noConfigurationFoundGo")}
        </Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("operations.configs.editConfig.title")}
      icon={<SettingsIcon />}
      headerActions={
        <Tabs
          value={activeTab}
          onChange={(_, value: ConfigTab) => navigateToTab(value)}
          aria-label={t("operations.configs.editConfig.title")}
        >
          <Tab value="summary" label={t("operations.configs.editConfig.tabs.summary")} />
          <Tab value="edit" label={t("operations.configs.editConfig.tabs.json")} />
        </Tabs>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {config && (
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {activeTab === "summary" && <ConfigSummary config={config} />}

            {activeTab === "edit" && (
              <AdminSectionPanel
                header={
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1} alignItems="center" color="primary.main">
                      <CodeIcon />
                      <Typography
                        variant="h6"
                        color="inherit"
                        sx={{ fontSize: { xs: "1.1rem", sm: "1.15rem" } }}
                      >
                        {t("operations.configs.editConfig.text.rawJsonConfiguration")}
                      </Typography>
                      {hasUnsavedChanges && (
                        <AppChip
                          tone="warning"
                          icon={<WarningAmberIcon />}
                          label={t("operations.configs.editConfig.text.unsavedChanges")}
                          size="small"
                        />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={() => openImageUploadDialog("general")}
                      >
                        {t("operations.configs.editConfig.actions.uploadImage")}
                      </Button>
                      <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={() => openImageUploadDialog("banner")}
                      >
                        {t("operations.configs.editConfig.actions.uploadBanner")}
                      </Button>
                    </Stack>
                  </Stack>
                }
              >
                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    boxSizing: "border-box",
                    height: 500,
                    minHeight: 280,
                    maxHeight: "80vh",
                    overflow: "auto",
                    resize: "vertical",
                    "& .cm-editor": { height: "100%" },
                  }}
                >
                  <CodeMirror
                    value={configString}
                    height="100%"
                    theme={githubLight}
                    extensions={jsonExtensions}
                    onChange={handleConfigChange}
                    basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: true }}
                  />
                </Paper>
                {jsonError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {jsonError}
                  </Alert>
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    mt: 2,
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || Boolean(jsonError) || !hasUnsavedChanges}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
                  >
                    {t("operations.configs.editConfig.actions.saveChanges")}
                  </Button>
                </Box>
              </AdminSectionPanel>
            )}
          </Stack>
        </form>
      )}
      <AdminDialog
        open={imageUploadDialog !== null}
        onClose={() => setImageUploadDialog(null)}
        title={
          imageUploadDialog === "general"
            ? t("operations.configs.editConfig.text.generalImageUpload")
            : t("operations.configs.editConfig.text.bannerImageUpload")
        }
        icon={<CloudUploadIcon />}
        error={uploadError}
        loading={isUploading}
        maxWidth="sm"
      >
        {isUploading ? (
          <Box
            sx={{ display: "flex", minHeight: 200, alignItems: "center", justifyContent: "center" }}
          >
            <CircularProgress aria-label={t("common.loading")} />
          </Box>
        ) : uploadedImageUrl ? (
          <Stack spacing={2}>
            <TextField
              fullWidth
              label={t("operations.configs.editConfig.fields.uploadedImageUrl")}
              value={uploadedImageUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t("operations.configs.editConfig.actions.copyUrl")}
                      onClick={handleCopyUploadedImageUrl}
                      edge="end"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {imageUploadDialog === "banner" && (
              <Alert severity="success">
                {t("operations.configs.editConfig.text.bannerImageAutomaticallySet")}
              </Alert>
            )}
          </Stack>
        ) : (
          <Box
            sx={{
              border: "1px dashed",
              borderColor: isDragOver ? "primary.main" : "divider",
              bgcolor: isDragOver ? "action.hover" : "transparent",
              borderRadius: 1,
              px: 3,
              py: 5,
              textAlign: "center",
              transition: "all 0.2s ease-in-out",
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragOver(false);
              handleUploadFileSelection(event.dataTransfer.files[0]);
            }}
          >
            <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 1.5 }} />
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {isDragOver
                ? t("common.imageUpload.text.dropImageHere")
                : t("common.imageUpload.text.dragDropImageHere")}
            </Typography>
            <MuiLink
              component="button"
              type="button"
              onClick={() => uploadInputRef.current?.click()}
              sx={{ cursor: "pointer" }}
            >
              {t("common.imageUpload.text.clickBrowseFiles")}
            </MuiLink>
            <input
              ref={uploadInputRef}
              type="file"
              hidden
              accept="image/*"
              onChange={(event) => {
                handleUploadFileSelection(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
          </Box>
        )}
      </AdminDialog>
    </PageShell>
  );
};

export default EditConfig;
